import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HeroCarousel } from "@/components/HeroCarousel";
import { TrustStrip } from "@/components/Certifications";
import { VideoSection } from "@/components/VideoSection";
import { ProductCard } from "@/components/ProductCard";
import { WhyChooseUs } from "@/components/Sections";
import { Testimonials } from "@/components/Testimonials";
import { PRODUCTS, HERBS, HERB_IMG_FALLBACK } from "@/data/catalog";

const FEATURED = ["absolute-power", "piles-care", "ortho"];

export default function Home() {
  const featured = FEATURED.map((s) => PRODUCTS.find((p) => p.slug === s)).filter(Boolean);

  return (
    <>
      <HeroCarousel />
      <TrustStrip />
      <VideoSection />

      <section data-testid="featured-products" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap items-end justify-between gap-4 mb-12"
          >
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C05C46] mb-3">
                Bestsellers
              </p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1c2826] mb-4">
                Ayurvedic Supplements for Every Need
              </h2>
              <p className="text-base text-[#4B5563] leading-relaxed">
                From digestive care to men's vitality and joint strength — every formula is 100%
                herbal, certified and made in Jaipur.
              </p>
            </div>
            <Link to="/products">
              <Button
                data-testid="view-all-products"
                variant="outline"
                className="rounded-full border-[#1A4331]/40 text-[#1A4331] hover:bg-[#1A4331] hover:text-white transition-colors"
              >
                View All 8 Products
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.09 }}
              >
                <ProductCard product={p} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <WhyChooseUs />

      <section data-testid="herbs-teaser" className="py-16 sm:py-20 bg-[#1A4331] text-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D4AF37] mb-3">
                Inside Every Capsule
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                The Power of 17 Super Herbs
              </h2>
            </div>
            <Link to="/herbs">
              <Button
                data-testid="explore-herbs"
                className="rounded-full bg-[#C05C46] hover:bg-[#a84d3a] text-white transition-colors"
              >
                Explore All Herbs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {HERBS.slice(0, 4).map((h) => (
              <Link
                key={h.name}
                to="/herbs"
                data-testid={`herb-teaser-${h.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group rounded-xl overflow-hidden border border-white/10 bg-white/5 hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="h-36 overflow-hidden">
                  <img
                    src={h.image}
                    alt={h.name}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = HERB_IMG_FALLBACK;
                    }}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                    <Leaf className="w-4 h-4 text-[#D4AF37]" /> {h.name}
                  </h3>
                  <p className="text-xs text-[#F9F6F0]/65 mt-1">{h.benefit}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />
    </>
  );
}
